/**
 * Storage write-time verification — the one gate every storage write passes.
 *
 * WHY THIS EXISTS
 *
 * 2026-09-07, host real (Arnoldo Ivan Morales Tejada, vehículo 01M1YJWAMR4MCXZPN2M2DDN06Y):
 * the object `front_right.jpg` reached the bucket but the row never did, and every
 * retry for seven hours failed with `new row violates row-level security policy`.
 * Production forensics (read-only, as his own identity, in a rolled-back
 * transaction) proved the database accepts his INSERT and UPDATE — the request
 * reached storage without a live session context. The telemetry line is exact:
 * `VEHICLE_IMAGE_FORBIDDEN` nine times, while PostgREST calls with the same
 * client succeeded in the same second. The same class degrades document
 * verification uploads (private `verification` bucket).
 *
 * The fix has three legs, all in this module so a storage write can never
 * bypass them again:
 *   1. SESSION GATE — before a write, the live session must exist and not be
 *      about to expire; if it is, refresh once. `getUser()` revalidates against
 *      the server; the session the request will actually send is the session we
 *      verified.
 *   2. DEFINITIVE EXISTENCE — `objectExistsInStorage` never guesses from a
 *      folder listing, a `search` param, or a metadata field (the heuristic that
 *      reported `STORAGE_OBJECT_MISSING` for an object that existed two seconds
 *      earlier). It asks for a signed URL and, failing that, HEADs the public
 *      object URL — the same primitive the CDN itself uses.
 *   3. RECOVERY, ONCE — an auth-denied storage error is retried exactly once
 *      after a forced session refresh. Only if that also fails does the caller
 *      surface the sign-out guidance, which is then honest.
 *
 * `scripts/validate-storage-upload-guard.mjs` enforces that no service calls
 * `supabase.storage.from(...).upload(...)` directly.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import { fetchWithTimeoutAdapter } from './fetch-with-timeout'
import {
  classifyStorageError,
  isAuthDeniedError,
  type StorageErrorLike,
} from './storage-error-classify'

export { isAuthDeniedError }
export type { StorageErrorLike }

/** Force a session refresh. Returns true when a live session exists afterwards. */
export async function refreshSessionOnce(): Promise<boolean> {
  const { data, error } = await supabase.auth.refreshSession()
  return !error && Boolean(data.session)
}

/**
 * The session the next request will actually send must be live. Throws
 * ServiceError.unauthorized when even a forced refresh cannot produce one —
 * at that point "cerrá sesión y volvé a entrar" is the truth.
 */
export async function ensureLiveStorageSession(): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const session = data.session
  if (session && (session.expires_at ?? 0) * 1000 > Date.now() + 60_000) return
  if (await refreshSessionOnce()) return
  throw ServiceError.unauthorized('No live session for storage write')
}

/**
 * What we can PROVE about an object after an upload: it exists, it is
 * definitively gone, or the answer is inconclusive.
 *
 * The third state is not a nicety. `missing` used to be the fallback for every
 * failure, so a storage consistency gap or a dropped request told a host their
 * file was gone while it sat in the bucket (2026-09-07: a host stranded seven
 * hours; 2026-09-21, TAGO-MOBILE-4E: object present, owner matched, the check
 * failed two seconds after upload). Only an explicit not-found answer is
 * `missing`.
 */
export type StorageObjectState = 'exists' | 'missing' | 'unknown'

const OBJECT_STATE_ATTEMPTS = 3
const OBJECT_STATE_BACKOFF_MS = [0, 250, 750]

/**
 * Does the object actually exist in the bucket? Definitive, never heuristic.
 *
 * Signed URL first (authoritative for public AND private buckets). Auth-denied
 * refreshes the session once and retries; a transient answer backs off and
 * retries; only a definitive not-found is `missing`. Last resort: HEAD the
 * public object URL, which needs no session and answers from the same CDN path
 * users load — it can only ADD proof of existence or an explicit 404, never
 * turn an inconclusive answer into `missing`.
 */
export async function storageObjectState(
  bucket: string,
  storagePath: string,
): Promise<StorageObjectState> {
  for (let attempt = 0; attempt < OBJECT_STATE_ATTEMPTS; attempt += 1) {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(storagePath, 60)
    if (!error && data?.signedUrl) return 'exists'

    const kind = classifyStorageError(error)
    if (kind === 'missing') return 'missing'
    if (kind === 'auth-denied' && (await refreshSessionOnce())) continue

    const wait = OBJECT_STATE_BACKOFF_MS[attempt] ?? 750
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait))
  }

  // Public URL: derived from the client itself (getPublicUrl builds the string,
  // it never touches the network). It can prove existence (public buckets) or
  // an explicit 404; any other answer stays inconclusive.
  const publicUrl = supabase.storage.from(bucket).getPublicUrl(storagePath).data?.publicUrl
  if (!publicUrl) return 'unknown'
  try {
    const response = await fetchWithTimeoutAdapter(publicUrl, { method: 'HEAD' })
    if (response.ok) return 'exists'
    if (response.status === 404) return 'missing'
  } catch {
    // Inconclusive by definition — never a missing file.
  }
  return 'unknown'
}

/**
 * Back-compat shim for callers that only ask "can I prove it is there?". A
 * `missing` or `unknown` answer both read as `false`; use `storageObjectState`
 * when the difference matters — it does right after an upload.
 */
export async function objectExistsInStorage(bucket: string, storagePath: string): Promise<boolean> {
  return (await storageObjectState(bucket, storagePath)) === 'exists'
}

export type UploadBytesResult = {
  error: StorageErrorLike
}

/**
 * Upload bytes with the session gate and one auth-denied recovery retry.
 * Every direct storage write in the app goes through this; the caller keeps
 * ownership of its own error mapping and messaging.
 */
export async function uploadBytesWithSessionRecovery(
  bucket: string,
  storagePath: string,
  bytes: Parameters<ReturnType<typeof supabase.storage.from>['upload']>[1],
  options: { contentType?: string; upsert?: boolean } = {},
): Promise<UploadBytesResult> {
  await ensureLiveStorageSession()

  let { error } = await supabase.storage.from(bucket).upload(storagePath, bytes, options)
  if (error && isAuthDeniedError(error)) {
    await refreshSessionOnce()
    const retry = await supabase.storage.from(bucket).upload(storagePath, bytes, options)
    error = retry.error
  }
  return { error }
}
